'use client';
import { HTMLAttributes, useEffect, Ref } from 'react';
import styles from './widget.module.scss';
import { switchCase } from '@/packages/switch';
import Script from 'next/script';
import { NflOptInForm } from '@/components/contentful/StaticContent/NflOptInForm';

interface WidgetAttribute extends HTMLAttributes<HTMLElement> {
    class: string;
    ref?: Ref<HTMLElement>;
}

declare module 'react' {
    // eslint-disable-next-line @typescript-eslint/no-namespace
    namespace JSX {
        interface IntrinsicElements extends HTMLElement {
            'sxm-promo-code-validation-widget': WidgetAttribute;
            'sxm-rflz-widget': WidgetAttribute;
            'sxm-flepz-widget': WidgetAttribute;
            'sxm-streaming-flepz-widget': WidgetAttribute;
            'sxm-ui-login-form': WidgetAttribute;
        }
    }
}

interface Props {
    widget: SiteAngularWidget;
    dynamicLoadScriptsUrl?: string;
}

const switchWidget = (
    tabsToShow: string[] | undefined,
    extraParams: string,
    redirectUrl: string,
    registrationUrl: string,
) =>
    switchCase<AngularWidgetType, React.JSX.Element>({
        Promocode: <sxm-promo-code-validation-widget class="widget-styles" />,
        RFLZ: <sxm-rflz-widget class="widget-styles" />,
        FLEPZ: <sxm-flepz-widget class="widget-styles" tabs-to-show={tabsToShow} extra-params={extraParams} />,
        'Streaming Flepz': <sxm-streaming-flepz-widget class="widget-styles" />,
        Login: (
            <>
                <sxm-ui-login-form
                    redirect-uri={redirectUrl}
                    registration-uri={registrationUrl}
                    class="widget-styles"
                />
                <Script src="/ns/cms/static/global/widget/ui-nav/sxm-ui-navigation.js" />
            </>
        ),
        'NFL RFLZ': (
            <div className={styles.nflRflz}>
                <sxm-rflz-widget enable-nfl-opt-in="true" class="widget-styles" />
            </div>
        ),
        'NFL FLEPZ': (
            <div className={styles.nflFlepz}>
                <sxm-flepz-widget
                    tabs-to-show={tabsToShow}
                    extra-params={extraParams}
                    enable-nfl-opt-in="true"
                    class="widget-styles"
                />
            </div>
        ),
        'NFL Opt-in Form': (
            <div>
                <NflOptInForm />
            </div>
        ),
    })(<></>);

export default function Widget({ widget, dynamicLoadScriptsUrl }: Props): React.JSX.Element {
    const tabsToShow = widget.fields.tabs;
    const extraParams = `${widget.fields.extra_params}  ${widget.fields.hide_tabs_nav}`;
    const redirectUrl = `${widget.fields.supplementary_link?.fields.href || ''}/account/manage`;
    const registrationUrl = `${widget.fields.supplementary_link?.fields.href || ''}/account/registration`;
    const type = widget.fields.widget_type as AngularWidgetType;
    const deCareScript = dynamicLoadScriptsUrl?.split(',').find((el) => el.includes('de-care-elements.js'));

    useEffect(() => {
        const script = document.createElement('script');
        const existingScript = document.querySelector(`script[src="${deCareScript}"]`);

        if (deCareScript && !existingScript) {
            script.src = deCareScript;
            script.type = 'module';
            document.body.appendChild(script);
        }

        return () => {
            if (script && script.parentNode) {
                script.parentNode.removeChild(script);
            }
        };
    }, [deCareScript]);

    return (
        <div className={styles.formContainer}>
            {switchWidget(tabsToShow, extraParams, redirectUrl, registrationUrl)(type)}
        </div>
    );
}
